import React, { useRef } from 'react';
import { X } from 'lucide-react';
import Text from './Text';
import IconButton from './IconButton';
import useClickOutside from '../../hooks/useClickOutside';

const Drawer = ({
  isOpen = false,
  onClose,
  title,
  children,
  position = 'right',
  size = 'md',
  showOverlay = true,
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = '',
  ...props
}) => {
  const drawerRef = useRef(null);

  useClickOutside(() => {
    if (closeOnOverlayClick && isOpen) onClose?.();
  }, drawerRef);

  const sizeClasses = {
    sm:   'w-64 sm:w-72',
    md:   'w-80 sm:w-96',
    lg:   'w-96 sm:w-[28rem]',
    xl:   'w-full sm:w-[32rem]',
    full: 'w-full',
  };

  const slideClass = {
    left:  isOpen ? 'translate-x-0' : '-translate-x-full',
    right: isOpen ? 'translate-x-0' : 'translate-x-full',
  };

  return (
    <>
      {/* Overlay */}
      {showOverlay && (
        <div
          className='fixed inset-0 transition-opacity duration-300 z-40 !mt-0'
          style={{
            background:    'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(4px)',
            opacity:       isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
          }}
          onClick={() => closeOnOverlayClick && onClose?.()}
          aria-hidden='true'
        />
      )}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby='drawer-title'
        className={`fixed top-0 ${position === 'left' ? 'left-0' : 'right-0'} h-screen ${sizeClasses[size]} flex flex-col transform transition-transform duration-300 ease-in-out z-40 !mt-0 ${slideClass[position]} ${className}`}
        style={{
          background: 'var(--bg-card)',
          borderLeft: position === 'right' ? '1px solid var(--border)' : 'none',
          borderRight: position === 'left' ? '1px solid var(--border)' : 'none',
          boxShadow: '0 0 48px rgba(0, 0, 0, 0.25)',
        }}
        {...props}
      >
        {/* Header */}
        <div
          className='flex items-center justify-between px-5 py-4'
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          {title && (
            <Text id='drawer-title' variant='title' className='!text-base'>
              {title}
            </Text>
          )}
          {showCloseButton && (
            <IconButton icon={X} onClick={onClose} tooltip='Close' className='ml-auto' variant='ghost' />
          )}
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto px-5 py-4 thin-scrollbar'>{children}</div>
      </div>
    </>
  );
};

const DrawerHeader = ({ children, className = '' }) => (
  <div className={`mb-4 ${className}`}>{children}</div>
);

const DrawerBody = ({ children, className = '' }) => (
  <div className={`space-y-4 ${className}`}>{children}</div>
);

const DrawerFooter = ({ children, className = '', align = 'right' }) => {
  const alignClass = {
    left:    'justify-start',
    center:  'justify-center',
    right:   'justify-end',
    between: 'justify-between',
  }[align];

  return (
    <div
      className={`flex gap-3 p-5 mt-0 ${alignClass} ${className}`}
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      {children}
    </div>
  );
};

Drawer.Header = DrawerHeader;
Drawer.Body   = DrawerBody;
Drawer.Footer = DrawerFooter;

export default Drawer;
